/*
 * R159zz · Feb 2026 — useProjectStatuses hook.
 *
 * Loads the workspace's merged status catalogue once per session and
 * exposes:
 *   • statusMeta   → { key: { label, cls } } — read-optimised map used
 *                    everywhere STATUS_META was previously hard-coded
 *   • canonicalKeys → array of the 12 built-in ids (rendering order)
 *   • refresh      → force reload after a settings save
 *
 * Every consumer reads via this hook so a Settings save instantly
 * propagates to the Planning Board / Projects list / Overview form
 * without a page refresh.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";

// Baked-in defaults (mirror `DEFAULT_STATUS_META` on the server). Used
// as fallback while the fetch is in-flight so cards don't render
// unstyled chips on first paint.
export const DEFAULT_STATUS_META = {
  active:        { label: "Active",         cls: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  planned:       { label: "Planned",        cls: "bg-blue-100 text-blue-700 border-blue-300" },
  in_progress:   { label: "In progress",    cls: "bg-sky-100 text-sky-700 border-sky-300" },
  hold:          { label: "Hold",           cls: "bg-amber-100 text-amber-700 border-amber-300" },
  paused:        { label: "Paused",         cls: "bg-amber-100 text-amber-700 border-amber-300" },
  awaiting_work: { label: "Awaiting Work",  cls: "bg-amber-50 text-amber-800 border-amber-200" },
  inactive:      { label: "Inactive",       cls: "bg-neutral-100 text-neutral-500 border-neutral-300" },
  at_risk:       { label: "At risk",        cls: "bg-orange-100 text-orange-700 border-orange-300" },
  blocked:       { label: "Blocked",        cls: "bg-rose-100 text-rose-700 border-rose-300" },
  done:          { label: "Done",           cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  idle:          { label: "Idle",           cls: "bg-slate-100 text-slate-700 border-slate-300" },
  archived:      { label: "Archived",       cls: "bg-neutral-100 text-neutral-500 border-neutral-300" },
};
export const CANONICAL_STATUS_KEYS = Object.keys(DEFAULT_STATUS_META);

export function useProjectStatuses(workspaceId) {
  const [statusMeta, setStatusMeta] = useState(DEFAULT_STATUS_META);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/workspaces/${workspaceId}/settings/project-statuses`);
      const map = data?.statuses || {};
      // Merge defaults with server result so any brand-new canonical
      // key added later still renders even before a workspace admin
      // has saved anything.
      const merged = { ...DEFAULT_STATUS_META };
      Object.entries(map).forEach(([k, v]) => {
        merged[k] = { label: v.label, cls: v.cls };
      });
      setStatusMeta(merged);
    } catch { /* silent — fall back to defaults */ }
    finally { setLoading(false); }
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  return { statusMeta, canonicalKeys: CANONICAL_STATUS_KEYS, loading, refresh: load };
}
